import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/lib/api';
import type { ApiError } from '@/lib/api';

export interface JournalEntry {
  _id: string;
  conversationId: string;
  summary: string;
  title: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateJournalEntryRequest {
  conversationId: string;
  summary: string;
  title: string;
  tags?: string[];
}

export interface UpdateJournalEntryRequest {
  id: string;
  summary?: string;
  title?: string;
  tags?: string[];
}

interface JournalState {
  entries: JournalEntry[];
  loading: boolean;
  error: string | null;
  currentEntry: JournalEntry | null;
}

const initialState: JournalState = {
  entries: [],
  loading: false,
  error: null,
  currentEntry: null,
};

// Create journal entry
export const createJournalEntry = createAsyncThunk<
  JournalEntry,
  CreateJournalEntryRequest,
  { rejectValue: string }
>('journal/create', async (entryData, { rejectWithValue }) => {
  try {
    const response = await api.post<JournalEntry>(
      `${process.env.NEXT_PUBLIC_API_URL}/journal/entries`,
      entryData
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to create journal entry'
    );
  }
});

// Get all journal entries
export const getJournalEntries = createAsyncThunk<
  JournalEntry[],
  void,
  { rejectValue: string }
>('journal/fetchAll', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get<JournalEntry[]>(
      `${process.env.NEXT_PUBLIC_API_URL}/journal/entries`
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to fetch journal entries'
    );
  }
});

// Update journal entry
export const updateJournalEntry = createAsyncThunk<
  JournalEntry,
  UpdateJournalEntryRequest,
  { rejectValue: string }
>('journal/update', async ({ id, ...updateData }, { rejectWithValue }) => {
  try {
    const response = await api.put<JournalEntry>(
      `${process.env.NEXT_PUBLIC_API_URL}/journal/entries/${id}`,
      updateData
    );
    return response.data;
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to update journal entry'
    );
  }
});

// Delete journal entry
export const deleteJournalEntry = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('journal/delete', async (entryId, { rejectWithValue }) => {
  try {
    await api.delete(
      `${process.env.NEXT_PUBLIC_API_URL}/journal/entries/${entryId}`
    );
    return entryId;
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to delete journal entry'
    );
  }
});

// Export journal entry
export const exportJournalEntry = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>('journal/export', async (entryId, { rejectWithValue }) => {
  try {
    const response = await api.get(
      `${process.env.NEXT_PUBLIC_API_URL}/journal/entries/${entryId}/export?format=pdf`,
      { responseType: 'blob' }
    );
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `journal-entry-${entryId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: unknown) {
    const err = error as ApiError;
    return rejectWithValue(
      err.response?.data?.message || 'Failed to export journal entry'
    );
  }
});

const journalSlice = createSlice({
  name: 'journal',
  initialState,
  reducers: {
    clearJournal: (state) => {
      state.entries = [];
      state.currentEntry = null;
      state.error = null;
      state.loading = false;
    },
    setCurrentEntry: (state, action) => {
      state.currentEntry = action.payload;
    },
    clearCurrentEntry: (state) => {
      state.currentEntry = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create journal entry
      .addCase(createJournalEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createJournalEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.entries.unshift(action.payload);
      })
      .addCase(createJournalEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to create journal entry';
      })
      // Get journal entries
      .addCase(getJournalEntries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getJournalEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.entries = action.payload;
      })
      .addCase(getJournalEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to fetch journal entries';
      })
      // Update journal entry
      .addCase(updateJournalEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateJournalEntry.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.entries.findIndex(
          (entry) => entry._id === action.payload._id
        );
        if (index !== -1) {
          state.entries[index] = action.payload;
        }
        if (state.currentEntry?._id === action.payload._id) {
          state.currentEntry = action.payload;
        }
      })
      .addCase(updateJournalEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to update journal entry';
      })
      // Delete journal entry
      .addCase(deleteJournalEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteJournalEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.entries = state.entries.filter(
          (entry) => entry._id !== action.payload
        );
        if (state.currentEntry?._id === action.payload) {
          state.currentEntry = null;
        }
      })
      .addCase(deleteJournalEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to delete journal entry';
      })
      // Export journal entry
      .addCase(exportJournalEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportJournalEntry.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportJournalEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to export journal entry';
      });
  },
});

export const { clearJournal, setCurrentEntry, clearCurrentEntry } =
  journalSlice.actions;
export default journalSlice.reducer;
